/**
 * PixModalIsland — substitui `#pix-modal` do V1 (delivery_app.html:2150+).
 *
 * Fluxo:
 *   1. CheckoutIsland cria o pedido como AGUARDANDO_PAGAMENTO (POST
 *      `/api/delivery/orders/save` com `force_status=AGUARDANDO_PAGAMENTO`)
 *   2. Chama POST `/api/delivery/orders/{id}/pay-online` que pede Mercado
 *      Pago um Pix dinâmico → backend devolve `qr_code` (copy-paste) +
 *      `qr_code_base64` (imagem)
 *   3. Dispatcha `aliv:open-pix-modal` com { orderId, qrCode, qrCodeBase64 }
 *   4. Cliente paga (escaneia QR ou copia código no app do banco)
 *   5. Mercado Pago notifica o backend via webhook → backend dispara
 *      WebSocket `payment_approved:<order_id>` pra todas as conexões da
 *      loja (V1 + V2)
 *   6. V2 escuta `aliv:payment-approved` (event que V1 dispatcha do
 *      socket.onmessage — paridade) E também faz fallback de polling
 *      manual via GET /check-payment a cada 5s caso WS falhe
 *   7. Quando aprovado: success overlay + clear cart + abre tracking
 *
 * Defesas:
 *   - Polling defensivo (5s) cobre caso WebSocket desconecte
 *   - Botão "Já paguei, conferir" pra forçar check manual
 *   - Botão "Cancelar" só FECHA o modal (o pedido fica como
 *     AGUARDANDO_PAGAMENTO no backend; cliente pode tentar de novo
 *     na tab Pedidos ou fechar e refazer)
 */
import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useModalOpen } from '../hooks/useAnyModalOpen';
import { trackPurchase } from '../utils/tracking';
import { t, useI18n } from '../i18n';
import { EnviarResumoWhatsApp } from './_EnviarResumoWhatsApp';

interface PixOpenDetail {
  orderId: number | string;
  wabizId?: string;
  qrCode: string;
  qrCodeBase64: string;
  total: number;
  /** Pra reaproveitar no purchase event quando confirmar. */
  deliveryFee?: number;
}

type PixState = 'waiting' | 'checking' | 'approved' | 'expired' | 'aborted';

declare global {
  interface Window {
    // CART vem do global.d.ts (CartLine[]). Não re-declarar aqui pra
    // não disparar TS2717 (merge conflitante de tipos).
    updateCartUI?: () => void;
    STORE_WHATSAPP?: string;
  }
}

function fmtBRL(v: number): string {
  return v.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
}

/** Limpeza compartilhada do CART V1+V2 (mesma estratégia do CheckoutIsland). */
function clearCartCrossV1V2() {
  try {
    if (Array.isArray(window.CART)) {
      window.CART.length = 0;
    } else {
      window.CART = [];
    }
    localStorage.removeItem('wabiz_cart');
    window.dispatchEvent(new CustomEvent('aliv:cart-updated'));
    window.updateCartUI?.();
  } catch (err) {
    console.warn('[v2 pix] cleanup carrinho falhou', err);
  }
}

export function PixModalIsland() {
  // assina a troca de idioma (ver i18n.ts)
  useI18n();
  const [visible, setVisible] = useState(false);
  const [state, setState] = useState<PixState>('waiting');
  const [orderId, setOrderId] = useState<number | string>('');
  const [wabizId, setWabizId] = useState<string>('');
  const [qrCode, setQrCode] = useState<string>('');
  const [qrCodeBase64, setQrCodeBase64] = useState<string>('');
  const [total, setTotal] = useState<number>(0);
  const [deliveryFee, setDeliveryFee] = useState<number>(0);
  const [copyState, setCopyState] = useState<'idle' | 'copied' | 'error'>('idle');
  const [statusMsg, setStatusMsg] = useState<string>('');
  // Overlay "Não consegui pagar" — opções: falar com a loja (WhatsApp)
  // ou ver meus pedidos. Paridade V1 handlePixAbort:13710.
  const [showHelp, setShowHelp] = useState(false);

  // Polling guard — para o setInterval quando aprovado/cancelado.
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const orderIdRef = useRef<number | string>('');
  // Número do pedido como a loja vê (APP-123) — é o transaction_id do purchase.
  const wabizIdRef = useRef<string>('');

  // ─── Open via custom event ─────────────────────────────────────────
  useEffect(() => {
    function onOpen(e: Event) {
      const d = (e as CustomEvent).detail as PixOpenDetail | undefined;
      if (!d) return;
      setOrderId(d.orderId);
      orderIdRef.current = d.orderId;
      setWabizId(d.wabizId || '');
      wabizIdRef.current = d.wabizId || '';
      setQrCode(d.qrCode || '');
      setQrCodeBase64(d.qrCodeBase64 || '');
      setTotal(Number(d.total) || 0);
      setDeliveryFee(Number(d.deliveryFee) || 0);
      setState('waiting');
      setStatusMsg('');
      setCopyState('idle');
      requestAnimationFrame(() => setVisible(true));
    }
    window.addEventListener('aliv:open-pix-modal', onOpen as EventListener);
    return () => window.removeEventListener('aliv:open-pix-modal', onOpen as EventListener);
  }, []);

  // ─── WebSocket confirmation (paridade V1 socket.onmessage) ─────────
  useEffect(() => {
    if (!visible || state !== 'waiting') return;
    function onApproved(e: Event) {
      const d = (e as CustomEvent).detail as { orderId?: string | number } | undefined;
      if (!d?.orderId) return;
      if (String(d.orderId) !== String(orderIdRef.current)) return;
      handleApproved('Pagamento aprovado pelo banco!');
    }
    window.addEventListener('aliv:payment-approved', onApproved as EventListener);
    return () => window.removeEventListener('aliv:payment-approved', onApproved as EventListener);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible, state]);

  // ─── Polling fallback (GET /check-payment a cada 5s) ───────────────
  useEffect(() => {
    if (!visible || state !== 'waiting' || !orderId) return;
    pollRef.current = setInterval(() => {
      void runCheckPayment(false);
    }, 5_000);
    return () => {
      if (pollRef.current) {
        clearInterval(pollRef.current);
        pollRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible, state, orderId]);

  // ─── Body lock + ESC bloqueado (cliente NÃO pode fechar via ESC
  //     enquanto espera; força usar os botões de ação pra ter clareza)
  useEffect(() => {
    if (!visible) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, [visible]);

  useModalOpen(visible, close);

  async function runCheckPayment(manual: boolean) {
    if (manual) {
      setState('checking');
      setStatusMsg('Conferindo no banco…');
    }
    try {
      const res = await fetch(`/api/delivery/orders/${orderId}/check-payment`);
      const data = (await res.json().catch(() => ({}))) as { paid?: boolean };
      if (data.paid) {
        handleApproved('Pagamento confirmado!');
      } else if (manual) {
        setState('waiting');
        setStatusMsg('Ainda não confirmado. Tente novamente em alguns segundos.');
      }
    } catch (err) {
      console.warn('[v2 pix] check-payment erro', err);
      if (manual) {
        setState('waiting');
        setStatusMsg('Erro ao conferir. Verifique sua conexão.');
      }
    }
  }

  function handleApproved(msg: string) {
    setState('approved');
    setStatusMsg(msg);
    // Para o polling
    if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
    // GTM purchase (paridade V1 handlePaymentSuccessViaWS — V1 não chamava
    // trackPurchaseHelper aqui mas no createUnifiedOrder antes; agora que
    // PIX cria pedido ANTES do paid, o purchase fica certo aqui).
    // Mesmo ID do purchase do pagamento na entrega (wabiz_id, "APP-123") e do
    // envio pelo SERVIDOR (services/compra_servidor.py): é ele que deixa a
    // Meta/GA4 juntarem navegador + servidor numa compra só. Antes o PIX
    // mandava o id numérico e o resto o APP-123.
    trackPurchase(wabizIdRef.current || orderIdRef.current || orderId, total, deliveryFee);
    // Vibra (PWA pattern UX)
    try {
      if ('vibrate' in navigator) navigator.vibrate?.([100, 50, 100]);
    } catch {
      /* */
    }
    // Limpa cart + dispatcha update pras outras ilhas
    clearCartCrossV1V2();
  }

  async function onCopy() {
    if (!qrCode) return;
    try {
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(qrCode);
        setCopyState('copied');
      } else {
        // Fallback: select + execCommand
        const el = document.getElementById('v2-pix-code-input') as HTMLInputElement | null;
        if (el) {
          el.select();
          el.setSelectionRange(0, qrCode.length);
          const ok = document.execCommand('copy');
          setCopyState(ok ? 'copied' : 'error');
        }
      }
    } catch (err) {
      console.warn('[v2 pix] copy falhou', err);
      setCopyState('error');
    }
    window.setTimeout(() => setCopyState('idle'), 2000);
  }

  function close() {
    setVisible(false);
  }

  function onAbort() {
    // Abre overlay "Não consegui pagar" — cliente escolhe entre falar
    // com a loja no WhatsApp ou ver os pedidos. Não fecha o modal aqui
    // — o usuário decide com clique.
    setShowHelp(true);
  }

  function openWhatsAppHelp() {
    const raw = window.STORE_WHATSAPP || '';
    const clean = raw.replace(/\D/g, '');
    if (!clean) {
      // Sem número configurado — pelo menos abre os pedidos.
      onHelpSeeOrders();
      return;
    }
    // V1 pré-fixa "55" e usa mensagem fixa (linha 13751).
    const withDdi = clean.length <= 11 ? `55${clean}` : clean;
    const orderRef = wabizId ? `#${wabizId}` : `(ID ${orderId})`;
    const msg = encodeURIComponent(
      `Olá! Fiz um pedido ${orderRef} pelo PIX mas tive um problema no pagamento. Podem me ajudar?`,
    );
    window.open(`https://wa.me/${withDdi}?text=${msg}`, '_blank');
    // Após abrir o WhatsApp, fecha o modal + abre Pedidos (paridade V1).
    setShowHelp(false);
    if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
    close();
    setTimeout(() => {
      window.dispatchEvent(
        new CustomEvent('aliv:open-user-area', { detail: { tab: 'orders' } }),
      );
    }, 250);
  }

  function onHelpSeeOrders() {
    setShowHelp(false);
    if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
    close();
    setTimeout(() => {
      window.dispatchEvent(
        new CustomEvent('aliv:open-user-area', { detail: { tab: 'orders' } }),
      );
    }, 250);
  }

  function onSuccessContinue() {
    close();
    setTimeout(() => {
      // Abre o OrderTrackingIsland com o pedido confirmado.
      window.dispatchEvent(
        new CustomEvent('aliv:open-order-tracking', {
          detail: { orderId, wabizId },
        }),
      );
    }, 250);
  }

  if (!visible) return null;

  return createPortal(
    <div
      className="fixed inset-0 z-[99999] flex items-end sm:items-center justify-center bg-black/70 transition-opacity duration-200"
      role="dialog"
      aria-modal="true"
      aria-label={t("Pagamento PIX")}
    >
      <div
        className={[
          'relative w-full sm:w-[420px] sm:max-w-[420px]',
          'bg-white sm:rounded-2xl rounded-t-2xl',
          'shadow-2xl overflow-hidden max-h-[92vh]',
          'flex flex-col',
          'transition-transform duration-300',
          visible ? 'translate-y-0 sm:scale-100' : 'translate-y-full sm:translate-y-4 sm:scale-95',
        ].join(' ')}
        style={{ transitionTimingFunction: 'cubic-bezier(0.16, 1, 0.3, 1)' }}
      >
        {/* Header */}
        <div className="px-5 pt-4 pb-3 border-b border-gray-100 flex items-center justify-between bg-white">
          <div className="flex items-center gap-2">
            <i className="fas fa-qrcode text-teal-600 text-lg" />
            <h2 className="text-base font-extrabold text-gray-900 leading-none">
              {t('Pague com PIX')}
            </h2>
          </div>
          {state === 'waiting' && (
            <span className="inline-flex items-center gap-1.5 text-[10px] font-bold text-amber-700 uppercase tracking-wide bg-amber-50 border border-amber-200 px-2 py-0.5 rounded-full">
              <span className="w-1.5 h-1.5 bg-amber-500 rounded-full animate-pulse" />
              {t('Aguardando')}
            </span>
          )}
          {state === 'checking' && (
            <span className="text-[10px] font-bold text-blue-700 uppercase tracking-wide">
              <i className="fas fa-spinner fa-spin mr-1" />
              {t('Conferindo')}
            </span>
          )}
          {state === 'approved' && (
            <span className="text-[10px] font-bold text-green-700 uppercase tracking-wide">
              <i className="fas fa-check-circle mr-1" />
              {t('Pago')}
            </span>
          )}
        </div>

        {/* Body */}
        <div className="overflow-y-auto flex-1 min-h-0 px-5 py-4">
          {state === 'approved' ? (
            <div className="flex flex-col items-center text-center py-6">
              <div className="w-20 h-20 rounded-full bg-green-100 text-green-600 flex items-center justify-center mb-4 animate-bounce">
                <i className="fas fa-check text-4xl" />
              </div>
              <h3 className="text-xl font-extrabold text-gray-900 mb-1">
                {t('Pagamento aprovado!')}
              </h3>
              {wabizId && (
                <p className="text-sm text-gray-600 mb-3">
                  Pedido{' '}
                  <span className="font-extrabold text-gray-900 tabular-nums">
                    #{wabizId}
                  </span>
                </p>
              )}
              <p className="text-[13px] text-gray-500 mb-6 leading-relaxed max-w-[280px]">
                {t('Já avisamos a cozinha. Acompanhe o status na aba "Pedidos".')}
              </p>
              {/* Some sozinho quando a loja não confirma pela mensagem do cliente. */}
              {orderId && (
                <div className="w-full mb-2">
                  <EnviarResumoWhatsApp orderId={orderId} />
                </div>
              )}
              <button
                type="button"
                onClick={onSuccessContinue}
                className="w-full bg-red-600 hover:bg-red-700 text-white font-bold py-3 rounded-xl shadow-md text-sm transition flex items-center justify-center gap-2"
              >
                <i className="fas fa-receipt" />
                {t('Acompanhar pedido')}
              </button>
            </div>
          ) : (
            <>
              {/* QR Code */}
              <div className="bg-gray-50 rounded-2xl p-4 flex items-center justify-center mb-4">
                {qrCodeBase64 ? (
                  <img
                    src={`data:image/png;base64,${qrCodeBase64}`}
                    alt={t("QR Code PIX")}
                    className="w-56 h-56 object-contain"
                  />
                ) : (
                  <div className="w-56 h-56 flex items-center justify-center text-gray-300">
                    <i className="fas fa-spinner fa-spin text-4xl" />
                  </div>
                )}
              </div>

              {/* Total */}
              {total > 0 && (
                <p className="text-center text-[13px] text-gray-600 mb-3">
                  Total:{' '}
                  <span className="font-extrabold text-gray-900 tabular-nums">
                    {fmtBRL(total)}
                  </span>
                </p>
              )}

              {/* Copia e cola */}
              <div className="bg-gray-50 border border-gray-200 rounded-xl p-2 mb-2 flex items-center gap-1">
                <input
                  id="v2-pix-code-input"
                  type="text"
                  value={qrCode}
                  readOnly
                  className="flex-1 bg-transparent text-[11px] font-mono text-gray-600 px-2 py-1 truncate outline-none"
                />
                <button
                  type="button"
                  onClick={onCopy}
                  className={[
                    'shrink-0 px-3 py-1.5 rounded-lg text-xs font-bold text-white transition flex items-center gap-1',
                    copyState === 'copied'
                      ? 'bg-emerald-600'
                      : copyState === 'error'
                      ? 'bg-red-600'
                      : 'bg-indigo-600 hover:bg-indigo-700',
                  ].join(' ')}
                >
                  <i
                    className={`fas ${
                      copyState === 'copied'
                        ? 'fa-check'
                        : copyState === 'error'
                        ? 'fa-times'
                        : 'fa-copy'
                    } text-xs`}
                  />
                  {copyState === 'copied'
                    ? t('Copiado!')
                    : copyState === 'error'
                    ? t('Erro')
                    : t('Copiar')}
                </button>
              </div>

              {/* Instruções */}
              <div className="bg-blue-50 border border-blue-100 rounded-xl p-3 mb-3 text-[11px] text-blue-800">
                <p className="font-bold mb-1">
                  <i className="fas fa-info-circle mr-1" />
                  {t('Como pagar')}
                </p>
                <ol className="list-decimal list-inside space-y-0.5">
                  <li>{t('Abra o app do seu banco')}</li>
                  <li>{t('Escolha PIX → "Pagar com QR Code" ou "Pix Copia e Cola"')}</li>
                  <li>{t('Escaneie ou cole o código')}</li>
                  <li>{t('Confirme o pagamento')}</li>
                </ol>
              </div>

              {/* Status / mensagem */}
              {statusMsg && (
                <p
                  className={[
                    'text-center text-[12px] mb-3',
                    state === 'checking' ? 'text-blue-600' : 'text-gray-500',
                  ].join(' ')}
                >
                  {statusMsg}
                </p>
              )}

              {/* Botões de ação */}
              <button
                type="button"
                onClick={() => void runCheckPayment(true)}
                disabled={state === 'checking'}
                className="w-full bg-green-600 hover:bg-green-700 text-white font-bold py-3 rounded-xl shadow-md text-sm transition flex items-center justify-center gap-2 disabled:opacity-60 mb-2"
              >
                {state === 'checking' ? (
                  <i className="fas fa-spinner fa-spin" />
                ) : (
                  <i className="fas fa-check" />
                )}
                {t('Já paguei, conferir')}
              </button>
              <button
                type="button"
                onClick={onAbort}
                className="w-full border border-gray-200 hover:bg-gray-50 text-gray-600 font-bold py-2.5 rounded-xl text-xs transition"
              >
                {t('Não consegui pagar')}
              </button>
              <p className="text-[10px] text-gray-400 text-center mt-2 leading-snug">
                {t('O pedido fica reservado por alguns minutos enquanto o pagamento não chega. Não dê refresh nessa tela.')}
              </p>
            </>
          )}
        </div>

        {/* Overlay "Não consegui pagar" — paridade V1 handlePixAbort:13710.
            2 opções claras: falar com a loja (WhatsApp) ou ver os
            pedidos. O pedido fica AGUARDANDO_PAGAMENTO no banco; loja
            pode liberar manual ou cliente pode tentar pagar depois. */}
        {showHelp && (
          <div
            className="absolute inset-0 z-[100] bg-white/95 backdrop-blur-sm flex flex-col items-center justify-center p-6 text-center"
            onClick={() => setShowHelp(false)}
          >
            <div
              className="w-full max-w-[320px] bg-white rounded-2xl shadow-2xl border border-gray-100 p-5"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="w-14 h-14 rounded-full bg-amber-100 text-amber-600 flex items-center justify-center mx-auto mb-3">
                <i className="fas fa-exclamation-circle text-2xl" />
              </div>
              <h4 className="font-extrabold text-gray-900 text-base mb-1">
                {t('Pagamento ainda pendente')}
              </h4>
              <p className="text-[12px] text-gray-600 mb-5 leading-relaxed">
                {t('Seu pedido foi salvo, mas ainda não foi liberado pra cozinha porque aguarda confirmação do PIX. Teve algum problema?')}
              </p>
              <div className="space-y-2">
                <button
                  type="button"
                  onClick={openWhatsAppHelp}
                  className="w-full bg-green-600 hover:bg-green-700 text-white font-bold py-3 rounded-xl shadow-md text-sm transition flex items-center justify-center gap-2"
                  style={{ WebkitTapHighlightColor: 'transparent' }}
                >
                  <i className="fab fa-whatsapp text-base" />
                  {t('Falar com a loja')}
                </button>
                <button
                  type="button"
                  onClick={onHelpSeeOrders}
                  className="w-full border border-gray-200 hover:bg-gray-50 text-gray-700 font-bold py-2.5 rounded-xl text-sm transition"
                >
                  {t('Ver meus pedidos')}
                </button>
                <button
                  type="button"
                  onClick={() => setShowHelp(false)}
                  className="w-full text-xs font-bold text-gray-500 hover:text-gray-700 py-1"
                >
                  {t('Voltar e tentar pagar')}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>,
    document.body,
  );
}
